import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Space,
  Tag,
  Input,
  Row,
  Col,
  message,
  Typography,
  Divider,
  Alert,
  Statistic,
  Progress,
  Badge,
  Timeline,
  Tooltip,
  Popconfirm,
  Button,
} from 'antd';
import {
  ScanOutlined,
  BarcodeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  TruckOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import type { PackingOrder } from '../../types/packing';
import apiService from '../../../../services/apiService';
import { playErrorSound } from '../../../../utils/soundUtils';

const { Title, Text } = Typography;

interface PickupScanInterfaceProps {
  onUpdateOrder?: () => void;
}

interface ScanResult {
  barcode: string;
  success: boolean;
  order?: PackingOrder;
  error?: string;
  timestamp: Date;
}

const PickupScanInterface: React.FC<PickupScanInterfaceProps> = ({
  onUpdateOrder
}) => {
  const [scanning, setScanning] = useState(true);
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([]);
  const [currentOrder, setCurrentOrder] = useState<PackingOrder | null>(null);
  const [scanInputValue, setScanInputValue] = useState('');
  
  // Scanner detection
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastScanTime, setLastScanTime] = useState(0);
  const [isRealScannerMode, setIsRealScannerMode] = useState(false);
  
  const scanInputRef = useRef<any>(null);

  // 與揀貨一致的條碼歸一化
  const normalize = (v?: string | null) =>
    String(v ?? '')
      .replace(/\s+/g, '')
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .replace(/[^0-9A-Za-z]/g, '');

  const handleClearScanHistory = () => {
    setScanHistory([]);
    message.success('已清空掃描歷史');
  };

  useEffect(() => {
    // Focus on scan input when component mounts
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, []);

  // Hardware barcode scanner integration
  useEffect(() => {
    let scannerTimeout: NodeJS.Timeout;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const currentTime = Date.now();
      
      // Check if this could be from a barcode scanner (rapid input)
      if (currentTime - lastScanTime < 100) {
        setIsRealScannerMode(true);
      }
      
      setLastScanTime(currentTime);

      // Handle Enter key (scanner suffix)
      if (event.key === 'Enter') {
        event.preventDefault();
        if (scanBuffer.trim().length > 0) {
          handleScan(scanBuffer.trim());
          setScanBuffer('');
          setIsRealScannerMode(false);
        }
        return;
      }

      // Handle regular characters (build barcode)
      if (event.key.length === 1) {
        event.preventDefault();
        setScanBuffer(prev => prev + event.key);
        
        // Clear buffer after delay (in case scanner doesn't send Enter)
        clearTimeout(scannerTimeout);
        scannerTimeout = setTimeout(() => {
          if (scanBuffer.length > 8) { // Minimum barcode length
            handleScan(scanBuffer.trim());
          }
          setScanBuffer('');
          setIsRealScannerMode(false);
        }, 300);
      }
    };

    // Add global keyboard listener for barcode scanner
    if (scanning) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(scannerTimeout);
    };
  }, [scanning, scanBuffer, lastScanTime]);

  // Visual feedback for scanner detection
  useEffect(() => {
    if (isRealScannerMode && scanBuffer.length > 0) {
      // Show visual feedback that scanner is detected
      const indicator = document.createElement('div');
      indicator.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #52c41a;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        z-index: 9999;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      `;
      indicator.textContent = `🔍 掃描中... ${scanBuffer}`;
      document.body.appendChild(indicator);
      
      setTimeout(() => {
        if (document.body.contains(indicator)) {
          document.body.removeChild(indicator);
        }
      }, 2000);
    }
  }, [isRealScannerMode, scanBuffer]);

  const handleScan = async (barcode: string) => {
    if (!barcode || !barcode.trim()) return;
    
    setScanning(true);
    
    try {
      const searchTerm = barcode.trim();
      const normalized = normalize(searchTerm);
      
      console.log('[PICKUP] 开始扫描，运单号:', searchTerm);
      
      // 步骤1: 先不带状态过滤，查找所有匹配的订单
      const packingResp: any = await apiService.getPackingOrders({ 
        search: searchTerm, 
        // 不限制状态，先找到订单
        limit: 10 
      });
      const allOrders = packingResp?.data || packingResp?.packings || [];
      
      console.log('[PICKUP] API返回订单数量:', allOrders.length);
      
      // 查找匹配的訂單（運單號、訂單號、追蹤號）
      const matchedOrder = allOrders.find((po: any) => {
        const orderNorm = normalize(po.orderNumber);
        const waybillNorm = normalize(po.waybillNumber);
        const packingNorm = normalize(po.packingNumber);
        const trackingNorm = normalize(po.trackingNumber);
        const match = orderNorm === normalized || 
               waybillNorm === normalized || 
               packingNorm === normalized ||
               trackingNorm === normalized;
        
        if (match) {
          console.log('[PICKUP] 找到匹配订单:', {
            id: po.id,
            orderNumber: po.orderNumber,
            waybillNumber: po.waybillNumber,
            trackingNumber: po.trackingNumber,
            status: po.status
          });
        }
        
        return match;
      });
      
      if (!matchedOrder) {
        console.error('[PICKUP] 未找到匹配订单');
        console.log('[PICKUP] 可用订单列表:', allOrders.map((o: any) => ({
          orderNumber: o.orderNumber,
          waybillNumber: o.waybillNumber,
          status: o.status
        })));
        throw new Error('未找到已包裝的訂單。請確認運單號是否正確或訂單是否已完成包裝');
      }

      console.log('[PICKUP] 订单当前状态:', matchedOrder.status);

      // 檢查訂單狀態是否為已包裝
      if (matchedOrder.status !== 'packed') {
        if (matchedOrder.status === 'ready_for_pickup') {
          throw new Error('該訂單已完成攬收，當前狀態為「待攬收」，無需重複操作');
        } else if (matchedOrder.status === 'packing') {
          throw new Error('該訂單尚在包裝中，請先完成打包操作後再進行攬收');
        } else if (matchedOrder.status === 'shipped') {
          throw new Error('該訂單已發貨，無法再進行攬收操作');
        } else {
          throw new Error(`訂單狀態為 ${getStatusText(matchedOrder.status)}，無法進行攬收操作。僅支持「已包裝」狀態的訂單`);
        }
      }

      // 更新訂單狀態為待攬收 (ready_for_pickup)
      await apiService.markPackingReady(matchedOrder.id);

      // 同時更新出庫單狀態
      if (matchedOrder.orderNumber) {
        try {
          await apiService.updateOutboundOrder(matchedOrder.orderNumber, { 
            status: 'READY_FOR_PICKUP' 
          });
        } catch (err) {
          console.error('更新出庫單狀態失敗:', err);
          // 不影響主流程
        }
      }

      const scanResult: ScanResult = {
        barcode: searchTerm,
        success: true,
        order: matchedOrder,
        timestamp: new Date(),
      };
      setScanHistory([scanResult, ...scanHistory]);
      setCurrentOrder(matchedOrder);

      message.success({
        content: `攬收成功！訂單 ${matchedOrder.orderNumber} 已更新為待攬收狀態`,
        duration: 3,
      });

      // 通知父組件刷新
      if (onUpdateOrder) {
        onUpdateOrder();
      }

      // 自動重置到乾淨的攬收頁面（延遲執行以便用戶看到完成訊息）
      setTimeout(() => {
        // 重置所有狀態到初始值
        setScanHistory([]);
        setCurrentOrder(null);
        setScanInputValue('');
        setScanBuffer('');
        setLastScanTime(0);
        setIsRealScannerMode(false);

        message.info('已重置攬收頁面，可以開始新的攬收', 3);
      }, 1500);
      
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || String(err ?? '系統錯誤');
      const scanResult: ScanResult = {
        barcode,
        success: false,
        error: errorMsg,
        timestamp: new Date(),
      };
      setScanHistory([scanResult, ...scanHistory]);
      playErrorSound();
      message.error(`掃描失敗：${errorMsg}`);
    } finally {
      setScanning(false);
      setTimeout(() => setScanning(true), 100);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'default',
      'packing': 'blue',
      'packed': 'green',
      'ready_for_pickup': 'orange',
      'shipped': 'purple',
      'delivered': 'success',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      'pending': '待處理',
      'packing': '包裝中',
      'packed': '已包裝',
      'ready_for_pickup': '待攬收',
      'shipped': '已發貨',
      'delivered': '已收貨',
    };
    return texts[status] || status;
  };

  return (
    <div style={{ padding: '24px' }}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <TruckOutlined /> 攬收掃描操作
        </Title>
        <Text type="secondary">
          請掃描運單條碼進行攬收確認
        </Text>
      </div>

      {/* Alert */}
      <Alert
        message="攬收操作說明"
        description="請掃描已包裝訂單的運單條碼。掃描成功後，訂單狀態將從「已包裝」更新為「待攬收」，表示訂單已準備好由配送團隊取件。"
        type="info"
        showIcon
        closable
        style={{ marginBottom: '24px' }}
      />

      {/* Main Content */}
      <Row gutter={16}>
        <Col span={16}>
          {/* Scan Input */}
          <Card
            title={
              <Space>
                <ScanOutlined />
                <span>掃描區域</span>
                {currentOrder && (
                  <Tag color={getStatusColor(currentOrder.status)}>
                    {getStatusText(currentOrder.status)}
                  </Tag>
                )}
              </Space>
            }
            style={{ marginBottom: '16px' }}
          >
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <BarcodeOutlined 
                style={{ 
                  fontSize: '64px', 
                  color: scanning ? '#1890ff' : '#ccc',
                  marginBottom: '16px'
                }} 
              />
              <div style={{ marginBottom: '24px' }}>
                <Input
                  ref={scanInputRef}
                  placeholder="掃描運單條碼..."
                  size="large"
                  prefix={<ScanOutlined />}
                  value={scanInputValue}
                  onChange={(e) => setScanInputValue(e.target.value)}
                  onPressEnter={() => {
                    handleScan(scanInputValue);
                  }}
                  autoFocus
                  style={{ maxWidth: '500px' }}
                />
              </div>
              <Text type="secondary">
                將掃描槍對準運單條碼，或手動輸入後按 Enter
              </Text>
            </div>
          </Card>

          {/* Current Order Info */}
          {currentOrder && (
            <Card
              title={
                <Space>
                  <InboxOutlined />
                  <span>當前訂單信息</span>
                </Space>
              }
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic 
                    title="訂單編號" 
                    value={currentOrder.orderNumber}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="運單編號" 
                    value={currentOrder.waybillNumber}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="客戶名稱" 
                    value={currentOrder.customerName}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Col>
                <Col span={12}>
                  <div>
                    <Text strong>收件人：</Text>
                    <Text>{currentOrder.receiverName}</Text>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Text strong>聯絡電話：</Text>
                    <Text>{currentOrder.receiverContact}</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Text strong>配送公司：</Text>
                    <Text>{currentOrder.deliveryCompany || '-'}</Text>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Text strong>包裹重量：</Text>
                    <Text>{currentOrder.packageWeight} kg</Text>
                  </div>
                </Col>
              </Row>
            </Card>
          )}
        </Col>

        <Col span={8}>
          {/* Scan History */}
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                <span>掃描歷史</span>
                <Badge count={scanHistory.length} />
              </Space>
            }
            extra={
              <Tooltip title="清空掃描歷史">
                <Popconfirm
                  title="確認清空掃描歷史？"
                  okText="清空"
                  cancelText="取消"
                  onConfirm={handleClearScanHistory}
                >
                  <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
                </Popconfirm>
              </Tooltip>
            }
          >
            <Timeline
              mode="left"
              style={{ maxHeight: '500px', overflowY: 'auto', wordBreak: 'break-word' }}
            >
              {scanHistory.map((scan, index) => (
                <Timeline.Item
                  key={index}
                  color={scan.success ? 'green' : 'red'}
                  dot={
                    scan.success ? 
                    <CheckCircleOutlined style={{ color: 'green' }} /> :
                    <ExclamationCircleOutlined style={{ color: 'red' }} />
                  }
                >
                  <div style={{ fontSize: '12px', padding: '2px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%'
                      }}
                    >
                      {scan.barcode}
                    </div>
                    <div style={{ color: '#666' }}>
                      {moment(scan.timestamp).format('HH:mm:ss')}
                    </div>
                    {scan.success && scan.order && (
                      <div style={{ color: 'green', marginTop: '4px' }}>
                        ✓ 訂單 {scan.order.orderNumber} 已更新為待攬收
                      </div>
                    )}
                    {!scan.success && (
                      <div
                        style={{
                          background: '#fff2f0',
                          border: '1px solid #ffccc7',
                          borderRadius: 4,
                          padding: 12,
                          marginTop: 4
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#cf1322' }}>
                          <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                          掃描失敗
                        </div>
                        <div style={{ marginTop: 6, color: '#cf1322' }}>
                          <span style={{ marginRight: 6 }}>❌</span>
                          {scan.error || '未知錯誤'}
                        </div>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {/* Statistics */}
          <Card
            title="今日統計"
            style={{ marginTop: '16px' }}
          >
            <Statistic 
              title="已攬收訂單" 
              value={scanHistory.filter(s => s.success).length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Divider />
            <Statistic 
              title="失敗次數" 
              value={scanHistory.filter(s => !s.success).length}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
            {scanHistory.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text strong>成功率</Text>
                  <Progress 
                    percent={Math.round((scanHistory.filter(s => s.success).length / scanHistory.length) * 100)}
                    status="active"
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                  />
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PickupScanInterface;
